import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { busyChipSx } from 'components/table/tableChipStyles';
import TableStatusChip from 'components/table/TableStatusChip';
import CurrencyAmount from 'components/currency/CurrencyAmount';

function DetailRow({ label, value }) {
  const empty = value == null || value === '';
  return (
    <Box sx={{ py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.35 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={empty ? 400 : 500}
        color={empty ? 'text.disabled' : 'text.primary'}
        component={typeof value !== 'string' && typeof value !== 'number' ? 'div' : 'p'}
      >
        {empty ? '—' : value}
      </Typography>
    </Box>
  );
}

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.node])
};

function FlagChips({ flags }) {
  const active = flags.filter((f) => f.on);
  if (!active.length) {
    return <Typography variant="body2" color="text.disabled">—</Typography>;
  }
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.5}>
      {active.map((f) => (
        <Chip key={f.label} label={f.label} size="small" variant="outlined" />
      ))}
    </Stack>
  );
}

FlagChips.propTypes = {
  flags: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      on: PropTypes.bool
    })
  ).isRequired
};

export default function StoreDetailsDialog({
  open,
  store,
  imgUrl,
  onClose,
  onEdit
}) {
  if (!store) return null;

  const logo = imgUrl(store.logo);
  const cover = imgUrl(store.cover_image);

  const serviceFlags = [
    { label: 'Dine-in', on: store.dining },
    { label: 'Takeaway', on: store.take_away },
    { label: 'Delivery', on: store.delivery },
    { label: 'Pure Veg', on: store.pure_veg }
  ];

  const paymentFlags = [
    { label: 'COD', on: store.cod_pay },
    { label: 'Online', on: store.online_pay },
    { label: 'Wallet', on: store.wallet_pay },
    { label: 'Card', on: store.card_pay }
  ];

  const created = store.created_at
    ? new Date(store.created_at).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : '—';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            component="img"
            src={logo || undefined}
            alt=""
            sx={{
              width: 56,
              height: 56,
              borderRadius: 1.5,
              objectFit: 'cover',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.100',
              display: logo ? 'block' : 'none'
            }}
          />
          {!logo && (
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 1.5,
                bgcolor: 'primary.lighter',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <StorefrontIcon color="primary" />
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700} noWrap>
              {store.title_en || '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Code: {store.store_code || '—'}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
              <TableStatusChip active={!!store.status} />
              <Chip label={store.busy ? 'Busy' : 'Free'} size="small" sx={busyChipSx(!!store.busy)} />
            </Stack>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {cover && (
          <Box
            component="img"
            src={cover}
            alt="Cover"
            sx={{
              width: '100%',
              maxHeight: 160,
              objectFit: 'cover',
              borderRadius: 2,
              mb: 2,
              border: '1px solid',
              borderColor: 'divider'
            }}
          />
        )}

        <Grid container spacing={3}>
          <Grid size={{xs:12, sm:6}}>
            <Typography variant="subtitle2" fontWeight={700} color="primary.dark" sx={{ mb: 1 }}>
              General
            </Typography>
            <DetailRow label="Name (Arabic)" value={store.title_ar} />
            <DetailRow label="Store type" value={store.StoreType?.type_name} />
            <DetailRow label="Branch" value={store.BranchGroup?.franchise_name} />
            <DetailRow label="Rating" value={store.rating != null ? `${store.rating}` : '—'} />
            <DetailRow label="Created" value={created} />
          </Grid>

          <Grid size={{xs:12, sm:6}}>
            <Typography variant="subtitle2" fontWeight={700} color="primary.dark" sx={{ mb: 1 }}>
              Contact & location
            </Typography>
            <DetailRow label="Primary contact" value={store.primary_contact} />
            <DetailRow label="Secondary contact" value={store.secondary_contact} />
            <DetailRow label="Address (EN)" value={store.address_en} />
            <DetailRow label="Address (AR)" value={store.address_ar} />
            <DetailRow
              label="Coordinates"
              value={
                store.latitude != null && store.longitude != null
                  ? `${Number(store.latitude).toFixed(5)}, ${Number(store.longitude).toFixed(5)}`
                  : ''
              }
            />
          </Grid>

          <Grid size={{xs:12, sm:6}}>
            <Typography variant="subtitle2" fontWeight={700} color="primary.dark" sx={{ mb: 1 }}>
              Timings & delivery
            </Typography>
            <DetailRow
              label="Timings"
              value={
                store.from_time && store.to_time ? `${store.from_time} – ${store.to_time}` : ''
              }
            />
            <DetailRow label="Min. purchase" value={store.minimum_purchase != null ? <CurrencyAmount value={store.minimum_purchase} /> : ''} />
            <DetailRow label="Delivery charge" value={store.delivery_charge != null ? <CurrencyAmount value={store.delivery_charge} /> : ''} />
            <DetailRow
              label="Delivery range"
              value={store.delivery_range != null ? `${store.delivery_range} km` : ''}
            />
            <DetailRow
              label="Commission"
              value={store.commission_rate != null ? `${store.commission_rate}%` : ''}
            />
          </Grid>

          <Grid size={{xs:12, sm:6}}>
            <Typography variant="subtitle2" fontWeight={700} color="primary.dark" sx={{ mb: 1 }}>
              Services & payment
            </Typography>
            <Box sx={{ py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.75 }}>
                Services
              </Typography>
              <FlagChips flags={serviceFlags} />
            </Box>
            <Box sx={{ py: 1.25 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.75 }}>
                Payment methods
              </Typography>
              <FlagChips flags={paymentFlags} />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={onClose} variant="outlined" sx={{ textTransform: 'none' }}>
          Close
        </Button>
        {onEdit ? (
          <Button onClick={onEdit} variant="contained" sx={{ textTransform: 'none' }}>
            Edit store
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

StoreDetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  store: PropTypes.object,
  imgUrl: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func
};
